import { useState, useEffect } from 'react';
import { Plus, Search, AlertCircle, Tag } from 'lucide-react';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { CreateProspectModal } from '../../components/admin/crm/CreateProspectModal';
import { EditProspectModal } from '../../components/admin/crm/EditProspectModal';
import { CRMProspect, getAllProspects, SPT_CONFIG, STATUS_LABELS } from '../../utils/crmScoringService';

export function CRMProspectsPage() {
  const [prospects, setProspects] = useState<CRMProspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSPT, setFilterSPT] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProspect, setEditingProspect] = useState<CRMProspect | null>(null);

  useEffect(() => {
    loadProspects();
  }, []);

  async function loadProspects() {
    try {
      const data = await getAllProspects();
      setProspects(data);
    } catch (error) {
      console.error('Error loading prospects:', error);
    } finally {
      setLoading(false);
    }
  }

  const [filterTag, setFilterTag] = useState<string>('all');

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = searchTerm === '' ||
      p.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sector?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nom?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTag = filterTag === 'all' || p.tag === filterTag;

    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchesSPT = filterSPT === 'all' || p.spt_score?.toString() === filterSPT;

    return matchesSearch && matchesStatus && matchesSPT && matchesTag;
  });

  function getSPTBadgeClasses(sptScore?: number) {
    const config = SPT_CONFIG[sptScore as keyof typeof SPT_CONFIG];
    if (!config) return 'bg-gray-100 text-gray-800';

    const colorMap = {
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800',
    };

    return colorMap[config.color as keyof typeof colorMap] || 'bg-gray-100 text-gray-800';
  }

  if (loading) {
    return (
      <AdminLayout title="Prospects" description="Liste complète des prospects">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Prospects"
      description={`${filteredProspects.length} prospect(s) au total`}
      action={{
        label: 'Nouveau Prospect',
        icon: Plus,
        onClick: () => setShowCreateModal(true),
      }}
    >
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, secteur, contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <select
                value={filterSPT}
                onChange={(e) => setFilterSPT(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous les SPT</option>
                <option value="4">SPT 4 (Critique)</option>
                <option value="3">SPT 3 (Élevé)</option>
                <option value="2">SPT 2 (Moyen)</option>
                <option value="1">SPT 1 (Faible)</option>
              </select>

              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous les tags</option>
                <option value="simulateur_landing">Simulateur landing</option>
                <option value="simulateur_tresorerie_landing">Simulateur trésorerie</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SPT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Survie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MRR Estimé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Probabilité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source / Tag
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProspects.map((prospect) => (
                <tr
                  key={prospect.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setEditingProspect(prospect)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{prospect.organization_name}</div>
                      {prospect.sector && <div className="text-sm text-gray-500">{prospect.sector}</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${getSPTBadgeClasses(prospect.spt_score)}`}>
                      {prospect.spt_score}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                      {STATUS_LABELS[prospect.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-900">
                      <AlertCircle size={14} />
                      {prospect.survival_months}m
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-green-700">
                      {prospect.estimated_mrr.toLocaleString('fr-FR')} €
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {Math.round((prospect.dynamic_probability || 0) * 100)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {prospect.prenom || prospect.contact_name
                        ? `${prospect.prenom || ''} ${prospect.nom || prospect.contact_name || ''}`.trim()
                        : '-'}
                    </div>
                    {prospect.contact_email && (
                      <div className="text-xs text-gray-500">{prospect.contact_email}</div>
                    )}
                    {prospect.fonction && (
                      <div className="text-xs text-gray-400">{prospect.fonction}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {prospect.tag ? (
                      <div className="flex items-center gap-1.5">
                        <Tag size={12} className="text-blue-500" />
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                          {prospect.tag}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">{prospect.source || '-'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredProspects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucun prospect trouvé</p>
            </div>
          )}
        </div>

        {showCreateModal && (
          <CreateProspectModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              loadProspects();
            }}
          />
        )}

        {editingProspect && (
          <EditProspectModal
            prospect={editingProspect}
            onClose={() => setEditingProspect(null)}
            onSuccess={() => {
              loadProspects();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
